import { logger, loggerEnabled } from '../../../../utils/common/logger'
import { DOMEvents } from '../../../../utils/dom'
import { Store } from '../../../../utils/storage/keys'
import { ISubscription, SSOScreen } from '../adapter'

export interface IPianoApiResponse {
    code: number
    count?: number
    data?: object[]
    limit: number
    offset: number
    total: number
    conversions?: IPianoConversion[]
}

export interface IPianoApiAccessItem {
    // `expire_date` is not always available in the `/access/list` response.
    // It's only available in `conversion/list` response
    // All already expired supscription terms are excluded from `/access/list` response
    expire_date?: number
    resource: {
        aid: string
        rid: string
    },
    term: IPianoTerm
    start_date: number
}

export interface IPianoResource {
    rid: string
    aid: string
    name: string
    description: string
    image_url?: string
}

export interface IPianoTerm {
    aid: string
    term_id: string
    resource?: IPianoResource
    name?: string
    description?: string
    type?: string
}

export interface IPianoUserAccess {
    granted: boolean
    start_date: number
    expire_date?: number
}

export interface IPianoConversion {
    aid: string
    term?: IPianoTerm
    term_conversion_id?: string
    type?: string
    user_access: IPianoUserAccess
}

export interface IWindow extends Window {
    tp: any
}

declare let window: IWindow

enum KruxEventId {
    // EVENT_NAME = 'EVENT_ID' - Same as configured in `Salesforce Audience Studio`.
    MODAL_VIEW = 'Obc7Bc4b',
    SUBSCRIPTION = 'Oe95getX'
}

enum PianoCheckoutState {
    CHECKOUT_COMPLETED = 'checkoutCompleted'
}

export abstract class BasePianoSubscriberService {
    protected openCheckoutAfterLogin = false
    protected pianoEnabled = true
    protected pianoPreconfigured: boolean

    protected pianoEndpoints = {
        access: '/access/list',
        conversions: '/conversion/list',
        register: '/conversion/registration/create'
    }
    protected pianoExpUrl = '//experience.tinypass.com/xbuilder/experience/load?aid='
    protected pianoSandboxUrl = '//sandbox.tinypass.com/xbuilder/experience/load?aid='
    protected pianoReady: Promise<boolean>
    protected registrantTermId: string
    protected userToken: string
    protected pianoUser: {
        email?: string
    }
    protected meterData: {
        expired?: boolean,
        maxViews?: number,
        views?: number
    }

    public abstract getAccess(): Promise<ISubscription[]>

    public abstract getConversions(): any

    /**
     * Close Piano modal
     */
    public closeCheckoutModal() {
        window.tp.offer.close()
    }

    /**
     * Open the Piano checkout modal
     */
    public showCheckoutModal(offerId: string, termId: string) {
        if (offerId && termId) {
            window.tp.push(['init', () => {
                window.tp.offer.startCheckout({
                    offerId,
                    termId
                })
            }])
        }
    }

    /**
     * Register a user for one-digital registrant access, if they don't have it yet.
     * @param aid: application aid required for Piano API call
     * @param isLoginRadius - true if the user is logged in via LoginRadius
     * https://docs.piano.io/api?endpoint=get~2F~2Fconversion~2Fregistration~2Fcreate
     */
    public async registerForOneDigital(aid: string) {
        const query = {
            aid,
            term_id: this.registrantTermId,
            user_token: this.userToken
        }
        await this.pianoCallApi(this.pianoEndpoints.register, query)
        window.tp.user.refreshAccessToken(true)
    }

    /**
     * Calls Piano API via global object.
     */
    public async pianoCallApi(endpoint: string, data?: any): Promise<IPianoApiResponse> {
        window.tp = window.tp || []
        logger('debug', 'PianoCallApi: LoginRadius user detected')
        await new Promise((resolve) => {
            window.tp.push(['init', () => {
                logger('debug', 'PianoCallApi: Piano ready')
                resolve()
            }])
        })

        return new Promise((resolve, reject) => {
            try {
                window.tp.api.callApi(endpoint, data, (res: IPianoApiResponse) => {
                    // tslint:disable-next-line:no-magic-numbers
                    if (res.code !== 200 && res.code !== 0) {
                        reject(new Error(`Piano API failure: ${JSON.stringify(res)}`))
                    } else {
                        resolve(res)
                    }
                })
            } catch (e) {
                reject(e)
            }
        })
    }

    /**
     * Adds piano callback handlers using the window.tp object
     * @param vendor - optional, used to specify that the vendor is loginradius to not add a loginRequired handler
     */
    public addPianoHandlers(vendor?: string) {
        // call the confirm_referral endpoint and send an analytic event for each new subscription
        // https://docs.piano.io/callbacks/#completeevent
        window.tp.push(['addHandler', 'checkoutComplete', () => {
            document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        krux: {
                            krx_event_id: KruxEventId.SUBSCRIPTION,
                            krx_event_type: 'subscription'
                        },
                        mparticle: {
                            mp_event_type: 'UserContent'
                        },
                        permutive: {}
                    },
                    event_name: 'subscription',
                }
            }))
            // remove profile to force update
            localStorage.removeItem(Store.ID)
        }])

        // send an analytic event for checkout modal
        // https://docs.piano.io/callbacks/#startevent
        window.tp.push(['addHandler', 'startCheckout', () => {
            document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        krux: {
                            keys: {
                                modal_name: 'modal_name'
                            },
                            krx_event_id: KruxEventId.MODAL_VIEW,
                            krx_event_type: 'subscription'
                        },
                        mparticle: {
                            keys: {
                                modal_name: 'modal_name'
                            },
                            mp_event_type: 'UserPreference'
                        },
                        permutive: {
                            keys: {
                                modal_name: 'modal_name'
                            }
                        }

                    },
                    event_name: 'modal_view',
                    modal_name: 'start_checkout'
                }
            }))
        }])

        // reload the page after completing checkout and closing the modal
        // https://docs.piano.io/callbacks/#closeevent
        window.tp.push(['addHandler', 'checkoutClose', (checkoutCloseData: any) => {
            if (checkoutCloseData.state === PianoCheckoutState.CHECKOUT_COMPLETED) {
                window.location.reload()
            }
        }])

        // https://docs.piano.io/callbacks/#experienceexecute
        window.tp.push(['addHandler', 'experienceExecute', this.handleExperienceExecute.bind(this)])

        // https://docs.piano.io/callbacks/#activeevent
        window.tp.push(['addHandler', 'meterActive', this.handleMeterActive.bind(this)])

        // send an analytic event for paywall limit modal
        // https://docs.piano.io/callbacks/#expiredevent
        window.tp.push(['addHandler', 'meterExpired', (meterData: any) => {
            document.dispatchEvent(new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        krux: {
                            keys: {
                                modal_name: 'modal_name'
                            },
                            krx_event_id: KruxEventId.MODAL_VIEW,
                            krx_event_type: 'subscription'
                        },
                        mparticle: {
                            keys: {
                                modal_name: 'modal_name'
                            },
                            mp_custom_flags: ['Google.NonInteraction', 'Google.Page'],
                            mp_event_type: 'UserPreference'
                        },
                        permutive: {
                            keys: {
                                modal_name: 'modal_name'
                            }
                        }
                    },
                    event_name: 'modal_view',
                    modal_name: 'paywall_limit_reached'
                }
            }))

            this.handleMeterExpired(meterData)
        }])

        if (vendor === 'loginradius') {
            window.tp.push(['addHandler', 'loginRequired', (params: any) => {
                // Redirect to login page
                const redirectUrl = location.origin
                const screen = (params && params.startScreen) ? params.startScreen : ''

                if (sessionStorage) {
                    sessionStorage.setItem(Store.REDIRECT_URL, location.href)
                    // store params of offer in storage before redirect
                    if (params && params.offerId && params.termId) {
                        sessionStorage.setItem(Store.PIANO_PARAMS, JSON.stringify(params))
                    }
                }
                if (screen === SSOScreen.LOG_IN) {
                    location.assign(`${redirectUrl}/sign-in`)
                } else {
                    location.assign(`${redirectUrl}/register`)
                }
            }])
        }
    }

    /**
     * Push Piano tags
     */
    public async pianoPushTags(isPremium: boolean, isPaywallWhitelisted: boolean) {
        const tags: string[] = []
        if (isPremium) {
            tags.push('premium_locked')
        }

        if (isPaywallWhitelisted) {
            tags.push('not_metered')
        } else {
            tags.push('metered')
        }

        await this.pianoReady

        return new Promise((resolve, reject) => {
            try {
                window.tp.push(['setTags', tags])
                resolve()
            } catch (e) {
                reject(e)
            }
        })
    }

    /**
     * Push product logo url for Piano templates
     */
    public pianoPushLogoUrl(logoUrl: string) {
        window.tp.push(['setCustomVariable', 'logoUrl', logoUrl])
        logger('debug', 'logoURL: ' + logoUrl)
    }

    protected handleExperienceExecute(experience: any) {
        this.pianoUser = {
            email: experience.user.email
        }
        this.logMeterData()
    }

    protected handleMeterActive(meterData: any) {
        this.meterData = {
            expired: false,
            maxViews: meterData.maxViews,
            views: meterData.views
        }
        this.logMeterData()
    }

    protected handleMeterExpired(meterData: any) {
        this.meterData = {
            expired: true,
            maxViews: meterData.maxViews,
            views: meterData.views
        }
        this.logMeterData()
    }

    protected logMeterData() {
        // log meter data in console, when all data is available
        // log only on stage/dev or if debug=logging param is in URL for prod
        if (this.pianoUser && this.meterData && (__NON_PROD || loggerEnabled())) {
            const isUserLoggedIn = this.pianoUser.email.toLowerCase() !== 'n/a'
            const logMessagePrefix = isUserLoggedIn ? 'Paywall' : 'Registration'
            const logMessageSuffix = this.meterData.expired ? 'hard gate' : 'article counter'
            const logUserEmail = isUserLoggedIn ? this.pianoUser.email : 'not logged in'
            const logMeterData = `${this.meterData.views} of ${this.meterData.maxViews} free metered articles read`
            const css = isUserLoggedIn ?
                'padding: 3px 10px 3px 10px; font-size:15px; background-color:green; color:white;' :
                'padding: 3px 10px 3px 10px; font-size:15px; background-color:red; color:black;'
            const message = `${logMessagePrefix} ${logMessageSuffix} - ${logUserEmail} - ${logMeterData}`
            console.log('%c%s', css, message)
        }
    }

}
